import Modal from 'antd/es/modal';
import React, { useCallback, useEffect, useState } from 'react';
import { ACCOUNT, UNIVERSITY_QUESTIONS } from '../../redux/actions/accountActions';
import { useDispatch, useSelector } from 'react-redux';
import { FIND_OR_CREATE_DIALOG } from '../../redux/actions/dialogsActions';
import AcceptTerms from '../AcceptTerms';
import { getUniversityId, isGlobalVersionTwo } from '../../helpers/Helpers';
import OptionalQuestions from '../../containers/Messaging/components/OptionalQuestions';
import { localStorageWrapper } from '../../helpers/storageFactory';
import store from '../../redux/store';
import { Button } from 'antd';

import { UNIVERSITY_IDS } from '../../common/accountIds';

const AcceptTermsPopup = ({ engagementId = null, engagementType = null }) => {
    const dispatch = useDispatch();
    const account = useSelector((state) => state.account);
    const { isOpened, ambassadorId, ambassadorName, chatName, university, styles, saveAcceptedTerms, isOnSendMessage } =
        useSelector((state) => state.acceptTermsPopup);
    const [isPrivacyChecked, setIsPrivacyChecked] = useState(isGlobalVersionTwo);
    const [isPrivacyChanged, setIsPrivacyChanged] = useState(false);
    const [isMarketingChecked, setIsMarketingChecked] = useState(isGlobalVersionTwo);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [step, setStep] = useState(1);
    const [secondStepData, setSecondStepData] = useState({});
    const [questionsErrors, setQuestionsErrors] = useState({});

    useEffect(() => {
        if (university && university.id) {
            dispatch({
                type: UNIVERSITY_QUESTIONS,
                payload: {
                    universityId: university.id,
                    onlyMandatory: true,
                    engagementId,
                    engagementType,
                },
            });
        }
    }, [university]);

    const handleAcceptTerms = useCallback(() => {
        if (!isPrivacyChecked) {
            setIsPrivacyChanged(true);
            return;
        }
        setStep(2);
    }, [isPrivacyChecked]);

    const handleSubmit = useCallback(() => {
        const errors = {};
        for (const item of account.questions) {
            if (!secondStepData.answers[item.id]) {
                errors[item.id] = 'Answer required';
            }
        }
        if (errors.length > 0) {
            setQuestionsErrors(errors);
            return;
        }
        setSaveInProgress(true);
        dispatch({
            type: ACCOUNT.CLOSE_ACCEPT_TERMS_POPUP,
        });
        let onSuccess = () => {};
        if (saveAcceptedTerms) {
            if (isOnSendMessage) {
                onSuccess = () => {
                    dispatch({
                        type: ACCOUNT.FIRE_ON_ACCEPTED_TERMS,
                        payload: { accepted: true },
                    });
                    store.dispatch({
                        type: ACCOUNT.RESET_QUESTIONS,
                    });
                };
            }
        } else {
            onSuccess = () => {
                dispatch({
                    type: FIND_OR_CREATE_DIALOG,
                    payload: {
                        student_id: ambassadorId,
                    },
                });
                store.dispatch({
                    type: ACCOUNT.RESET_QUESTIONS,
                });
            };
        }
        const onFinish = () => {
            setSaveInProgress(false);
        };

        let utmData = {};
        const universityId = university.id;
        const uniOrGroupId = getUniversityId();
        if (localStorageWrapper.getItem('utmData_' + uniOrGroupId)) {
            utmData = JSON.parse(localStorageWrapper.getItem('utmData_' + uniOrGroupId));
        }

        utmData.university_id = universityId;

        const answers = [];
        for (const key in secondStepData.answers) {
            if (secondStepData.answers[key].answer !== '') {
                answers.push(secondStepData.answers[key]);
            }
        }

        dispatch({
            type: ACCOUNT.SAVE_ACCEPTED_TERMS,
            payload: {
                ...utmData,
                universityId: university.id,
                marketing_preference: isMarketingChecked,
                answers,
                engagementId,
                engagementType,
            },
            meta: { onSuccess, onFinish },
        });
    }, [account, dispatch, isMarketingChecked, secondStepData, university]);

    const handleCloseAcceptTermsPopup = useCallback(() => {
        dispatch({
            type: ACCOUNT.CLOSE_ACCEPT_TERMS_POPUP,
        });
    }, [dispatch]);

    const handlePrivacyChange = useCallback((elem) => {
        setIsPrivacyChecked(elem.target.checked);
    }, []);

    const handleMarketingChange = useCallback((elem) => {
        setIsMarketingChecked(elem.target.checked);
    }, []);

    const handleFieldUpdate = useCallback(
        (formattedAnswer, answer, rawAnswer, questionType, questionId, isMandatory) => {
            const updatedAnswers = Object.assign({}, secondStepData.answers, formattedAnswer);
            const updatedRawAnswers = Object.assign({}, secondStepData.rawAnswers, rawAnswer);
            const data = {
                answers: updatedAnswers,
                rawAnswers: updatedRawAnswers,
            };

            // Validate country field for Griffith University
            if (
                questionType === 'residence_country' &&
                (university.id === UNIVERSITY_IDS.GRIFFITH || university.id === UNIVERSITY_IDS.TEST_UNI_2)
            ) {
                data.country = answer;
            }

            if (isMandatory) {
                data[questionId] = answer;
            }

            setSecondStepData(data);
        },
        [secondStepData, university]
    );

    const marginTop =
        styles && styles.marginTop
            ? {
                  marginTop: styles.marginTop < 0 ? 0 : styles.marginTop,
              }
            : { marginTop: 60 };

    if (!university) {
        return null;
    }

    return (
        <Modal
            open={isOpened}
            styles={{ body: { padding: 0, ...styles, ...marginTop } }}
            width={470}
            footer={null}
            closable={true}
            maskClosable={true}
            onCancel={handleCloseAcceptTermsPopup}
            style={{ top: '30px' }}
        >
            <div className="sign-up-1 accept-terms padding">
                {step === 1 ? (
                    <>
                        <h2 className="modal-name" style={{ paddingBottom: '1rem' }}>
                            Before you start
                        </h2>
                        <p className="accept-terms-body">
                            You are already signed in but in order for you to start chatting{' '}
                            {chatName ? 'in ' + chatName : 'with ' + ambassadorName} you have to be aware that your
                            information will be provided to the {university.name || 'university'} according to it's
                            privacy policy.
                        </p>

                        <AcceptTerms
                            minimumAge={13}
                            universityName={university.name}
                            universityId={university.id}
                            privacyLink={university.privacy_policy}
                            isPrivacyChecked={isPrivacyChecked}
                            isPrivacyChanged={isPrivacyChanged}
                            isMarketingChecked={isMarketingChecked}
                            handlePrivacyChange={handlePrivacyChange}
                            handleMarketingChange={handleMarketingChange}
                        />
                    </>
                ) : (
                    <>
                        <h2 className="modal-name" style={{ paddingBottom: '1rem' }}>
                            Before you start chatting {chatName ? 'in ' + chatName : 'to ' + ambassadorName}
                        </h2>

                        <span className="modal-subtext">
                            Please answer these optional questions of {university.name}.
                        </span>
                        <OptionalQuestions
                            data={account.questions}
                            university={university}
                            universityId={university.id}
                            onFieldUpdate={handleFieldUpdate}
                            answers={secondStepData.rawAnswers}
                            minimumAge={13}
                            errors={questionsErrors}
                        />
                    </>
                )}

                <Button
                    type="submit"
                    className="sign-up-sbm sign-in-sbm"
                    onClick={
                        step === 1 && account.questions && account.questions.length > 0
                            ? handleAcceptTerms
                            : handleSubmit
                    }
                    disabled={saveInProgress}
                >
                    {step === 1 && account.questions && account.questions.length > 0 ? 'Continue' : 'Submit'}
                </Button>
            </div>
        </Modal>
    );
};

export default AcceptTermsPopup;
